// src/components/orders/OrderAttachments.jsx
/**
 * Order Attachments Component
 * Reusable component for managing file attachments on Purchase Orders
 * 
 * Features:
 * - Upload files (drag & drop or click)
 * - View/download attachments
 * - Delete attachments (admin only)
 * - Progress bar for uploads
 * - Client-side validation
 * 
 * Props:
 * - orderId: number (required) - The order ID to manage attachments for
 * - readOnly: boolean - If true, hides upload/delete buttons
 * - canDelete: boolean - If true, shows delete button (for admin role)
 * - onAttachmentChange: function - Callback when attachments change
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Spin, Alert, Tooltip, Modal, message } from 'antd';
import { 
    PaperClipOutlined, 
    UploadOutlined, 
    EyeOutlined, 
    DownloadOutlined, 
    DeleteOutlined,
    FileImageOutlined,
    FilePdfOutlined,
    InboxOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import {
    uploadAttachment,
    getAttachments,
    getDownloadUrl,
    viewAttachment,
    downloadAttachment,
    deleteAttachment,
    validateFile,
    formatFileSize,
    getFileIconType,
    ATTACHMENT_CONSTRAINTS
} from '../../services/orderAttachmentsService';
import './OrderAttachments.css';

const OrderAttachments = ({ 
    orderId, 
    readOnly = false, 
    canDelete = true,
    onAttachmentChange = null 
}) => {
    // State
    const [attachments, setAttachments] = useState([]);
    const [count, setCount] = useState(0);
    const [maxAllowed, setMaxAllowed] = useState(ATTACHMENT_CONSTRAINTS.MAX_FILES_PER_ORDER);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    // Refs
    const fileInputRef = useRef(null);

    // Fetch attachments on mount and when orderId changes
    const fetchAttachments = useCallback(async () => {
        if (!orderId) return;

        try {
            setLoading(true);
            setError(null);
            const response = await getAttachments(orderId);
            
            if (response.success && response.data) {
                setAttachments(response.data.attachments || []);
                setCount(response.data.count || 0);
                setMaxAllowed(response.data.max_allowed || ATTACHMENT_CONSTRAINTS.MAX_FILES_PER_ORDER);
            }
        } catch (err) {
            console.error('Error fetching attachments:', err);
            // Don't show error if order has no attachments yet (404)
            if (err.response?.status !== 404) {
                setError('Error al cargar los archivos adjuntos');
            }
            setAttachments([]);
            setCount(0);
        } finally {
            setLoading(false);
        }
    }, [orderId]);

    useEffect(() => {
        fetchAttachments();
    }, [fetchAttachments]);

    // Handle file selection
    const handleFileSelect = async (file) => {
        if (!file || uploading) return;

        // Validate file
        const validation = validateFile(file, count);
        if (!validation.valid) {
            setError(validation.error);
            return;
        }

        // Clear previous error
        setError(null);
        setUploading(true);
        setUploadProgress(0);

        try {
            const response = await uploadAttachment(orderId, file, (progress) => {
                setUploadProgress(progress);
            });

            if (response.success) {
                message.success('Archivo subido exitosamente');
                await fetchAttachments();
                
                // Notify parent component
                if (onAttachmentChange) {
                    onAttachmentChange('upload', response.data?.attachment);
                }
            }
        } catch (err) {
            console.error('Upload error:', err);
            const errorMessage = err.response?.data?.message || 'Error al subir el archivo';
            setError(errorMessage);
            message.error(errorMessage);
        } finally {
            setUploading(false);
            setUploadProgress(0);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // Handle file input change
    const handleInputChange = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    // Handle drag and drop
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!readOnly && !uploading && count < maxAllowed) {
            setIsDragging(true);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (readOnly || uploading || count >= maxAllowed) return;

        const file = e.dataTransfer.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    // Handle view attachment
    const handleView = async (attachment) => {
        try {
            message.loading({ content: 'Abriendo archivo...', key: 'viewFile', duration: 0 });
            await viewAttachment(orderId, attachment.id, attachment.mime_type);
            message.destroy('viewFile');
        } catch (err) {
            message.destroy('viewFile');
            message.error('Error al abrir el archivo');
        }
    };

    // Handle download attachment
    const handleDownload = async (attachment) => {
        try {
            await downloadAttachment(orderId, attachment.id, attachment.original_name);
            message.success('Descarga iniciada');
        } catch (err) {
            message.error('Error al descargar el archivo');
        }
    };

    // Handle delete attachment
    const handleDelete = (attachment) => {
        Modal.confirm({
            title: '¿Eliminar archivo adjunto?',
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <p>¿Está seguro que desea eliminar el archivo:</p>
                    <p><strong>{attachment.original_name}</strong></p>
                    <p style={{ color: '#ff4d4f', fontSize: '12px' }}>
                        Esta acción no se puede deshacer.
                    </p>
                </div>
            ),
            okText: 'Sí, eliminar',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    const response = await deleteAttachment(orderId, attachment.id);
                    if (response.success) {
                        message.success('Archivo eliminado exitosamente');
                        await fetchAttachments();
                        
                        // Notify parent component
                        if (onAttachmentChange) {
                            onAttachmentChange('delete', attachment);
                        }
                    }
                } catch (err) {
                    const errorMessage = err.response?.data?.message || 'Error al eliminar el archivo';
                    message.error(errorMessage);
                }
            },
        });
    };

    // Get file icon component
    const getFileIcon = (fileType) => {
        const iconType = getFileIconType(fileType);
        if (iconType === 'image') {
            return <FileImageOutlined />;
        }
        return <FilePdfOutlined />;
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-AR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Check if upload is allowed
    const canUpload = !readOnly && !uploading && count < maxAllowed;

    // Render loading state
    if (loading) {
        return (
            <div className="order-attachments">
                <div className="attachments-loading">
                    <Spin tip="Cargando archivos adjuntos..." />
                </div>
            </div>
        );
    }

    return (
        <div className="order-attachments">
            {/* Header */}
            <div className="order-attachments-header">
                <h4>
                    <PaperClipOutlined />
                    Archivos Adjuntos
                    <span className="attachments-count">({count}/{maxAllowed})</span>
                </h4>
            </div>

            {/* Error Alert */}
            {error && (
                <Alert
                    message={error}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setError(null)}
                    className="attachment-error"
                />
            )}

            {/* Upload Section */}
            {!readOnly && (
                <>
                    {count >= maxAllowed ? (
                        <div className="upload-disabled-message">
                            <ExclamationCircleOutlined style={{ marginRight: 8 }} />
                            Máximo de {maxAllowed} archivos alcanzado. Elimine uno para subir más.
                        </div>
                    ) : (
                        <div className="upload-section">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".jpg,.jpeg,.png,.pdf"
                                onChange={handleInputChange}
                                style={{ display: 'none' }}
                                disabled={!canUpload}
                            />
                            <div
                                className={`upload-dragger ${isDragging ? 'dragging' : ''} ${!canUpload ? 'disabled' : ''}`}
                                onClick={() => canUpload && fileInputRef.current?.click()}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                            >
                                <div className="upload-icon">
                                    {uploading ? <Spin /> : <InboxOutlined />}
                                </div>
                                <div className="upload-text">
                                    {uploading 
                                        ? 'Subiendo archivo...' 
                                        : 'Haga clic o arrastre un archivo aquí'}
                                </div>
                                <div className="upload-hint">
                                    JPG, PNG o PDF. Máximo 10MB.
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Upload Progress */}
                    {uploading && (
                        <div className="upload-progress">
                            <div className="progress-bar">
                                <div 
                                    className="progress-fill" 
                                    style={{ width: `${uploadProgress}%` }}
                                />
                            </div>
                            <div className="progress-text">
                                {uploadProgress}% completado
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Attachments List */}
            <div className="attachments-list">
                {attachments.length === 0 ? (
                    <div className="no-attachments">
                        <div className="no-attachments-icon">
                            <PaperClipOutlined />
                        </div>
                        <div>No hay archivos adjuntos</div>
                    </div>
                ) : (
                    attachments.map((attachment) => (
                        <div key={attachment.id} className="attachment-item">
                            {/* File Icon */}
                            <div className={`file-icon ${getFileIconType(attachment.file_type)}`}>
                                {getFileIcon(attachment.file_type)}
                            </div>

                            {/* File Info */}
                            <div className="file-info">
                                <Tooltip title={attachment.original_name}>
                                    <span className="file-name">
                                        {attachment.original_name}
                                    </span>
                                </Tooltip>
                                <div className="file-meta">
                                    {attachment.file_size_formatted || formatFileSize(attachment.file_size)}
                                    {' • '}
                                    {formatDate(attachment.uploaded_at)}
                                </div>
                            </div>

                            {/* File Actions */}
                            <div className="file-actions">
                                <Tooltip title="Ver">
                                    <Button
                                        size="small"
                                        icon={<EyeOutlined />}
                                        onClick={() => handleView(attachment)}
                                        className="btn-view"
                                    />
                                </Tooltip>
                                <Tooltip title="Descargar">
                                    <Button
                                        size="small"
                                        icon={<DownloadOutlined />}
                                        onClick={() => handleDownload(attachment)}
                                        className="btn-download"
                                    />
                                </Tooltip>
                                {canDelete && !readOnly && (
                                    <Tooltip title="Eliminar">
                                        <Button
                                            size="small"
                                            icon={<DeleteOutlined />}
                                            onClick={() => handleDelete(attachment)}
                                            className="btn-delete"
                                            danger
                                        />
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default OrderAttachments;
